import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

export const NATIVE_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf"];

const ZXING_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
];

export function createZxingReader() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });
}

export function hasNativeDetector() {
  return typeof window !== "undefined" && typeof window.BarcodeDetector === "function";
}

export async function createNativeDetector() {
  if (!hasNativeDetector()) return null;
  try {
    const supported = (await window.BarcodeDetector.getSupportedFormats?.()) || NATIVE_FORMATS;
    const formats = NATIVE_FORMATS.filter((f) => supported.includes(f));
    if (!formats.length) return null;
    return new window.BarcodeDetector({ formats });
  } catch (e) {
    console.warn("[scanner] BarcodeDetector unavailable:", e);
    return null;
  }
}

function gtinChecksumOk(code) {
  const digits = code.split("").map(Number);
  const check = digits.pop();
  let sum = 0;
  digits.reverse().forEach((d, i) => {
    sum += d * (i % 2 === 0 ? 3 : 1);
  });
  return (10 - (sum % 10)) % 10 === check;
}

function upceToUpca(upce) {
  if (upce.length !== 8) return null;
  const ns = upce[0];
  const d = upce.slice(1, 7);
  const last = d[5];
  let body;
  if ("012".includes(last)) body = d.slice(0, 2) + last + "0000" + d.slice(2, 5);
  else if (last === "3") body = d.slice(0, 3) + "00000" + d.slice(3, 5);
  else if (last === "4") body = d.slice(0, 4) + "00000" + d[4];
  else body = d.slice(0, 5) + "0000" + last;
  return ns + body + upce[7];
}

// Returns a normalized GTIN string or null when the read is not a plausible product code.
export function normalizeProductCode(raw, format) {
  const code = String(raw || "").replace(/\D/g, "");
  if (!code) return null;
  if (typeof format === "number") format = BarcodeFormat[format];
  const fmt = String(format || "").toLowerCase().replace("-", "_");
  if (fmt === "upc_e" && code.length === 8) {
    const upca = upceToUpca(code);
    return upca && gtinChecksumOk(upca) ? upca : null;
  }
  if (![8, 12, 13].includes(code.length)) return null;
  if (gtinChecksumOk(code)) return code;
  if (code.length === 8) {
    const upca = upceToUpca(code);
    if (upca && gtinChecksumOk(upca)) return upca;
  }
  return null;
}

export function isValidProductCode(raw) {
  return normalizeProductCode(raw) !== null;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function toCanvas(img, maxSide) {
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

async function decodeSource(source, detector, reader) {
  if (detector) {
    try {
      const codes = await detector.detect(source);
      for (const c of codes) {
        const n = normalizeProductCode(c.rawValue, c.format);
        if (n) return n;
      }
    } catch (e) {
      console.warn("[scanner] native image detect failed:", e);
    }
  }
  try {
    const result = await reader.decodeFromCanvas(source);
    return normalizeProductCode(result.getText(), result.getBarcodeFormat());
  } catch {
    return null;
  }
}

// Decodes a still photo: original size first, then downscaled to 1600px.
export async function decodeImageFile(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const detector = await createNativeDetector();
    const reader = createZxingReader();
    console.log("[scanner] photo decode via", detector ? "BarcodeDetector+zxing" : "zxing", `${img.naturalWidth}x${img.naturalHeight}`);
    const candidates = [toCanvas(img, Infinity)];
    if (Math.max(img.naturalWidth, img.naturalHeight) > 1600) candidates.push(toCanvas(img, 1600));
    for (const canvas of candidates) {
      const code = await decodeSource(canvas, detector, reader);
      if (code) return code;
    }
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
